import {
  AgGridReact,
  type CustomCellRendererProps,
} from '@mint-lib/ag-grid/react';
import { memo, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  ModuleRegistry,
  type ColDef,
} from '@mint-lib/ag-grid/community';
import { RowGroupingModule } from '@mint-lib/ag-grid/enterprise';
import { useTranslation } from '@mint-lib/i18n';
import { Settings } from '@mint-lib/icons';
import {
  Chip,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type {
  ChannelCatalogItem,
  GlossaryItem,
  GlossaryListQueryResult,
  PlatformCatalogItem,
} from '../../api/types.js';
import { useTruncatedList } from '../../hooks/index.js';
import { useIsReadOnlyUser } from '../../hooks/useIsReadOnlyUser.js';
import { normalizeString } from '../../utils/transforms.js';
import { GridEmptyState } from '../atoms/GridEmptyState.jsx';
import { Icon } from '../atoms/Icon.jsx';
import { GlossaryAliasesForm } from './GlossaryAliasesForm.jsx';

ModuleRegistry.registerModules([
  CellStyleModule,
  ColumnAutoSizeModule,
  RowGroupingModule,
  ClientSideRowModelModule,
]);

interface GlossaryRowData {
  id: string;
  name: string;
  label: string;
  type: 'channel' | 'platform';
  group: string;
  logo: ReactNode | null;
  aliases: Array<{ id: string; name: string }>;
  glossaryItem: GlossaryItem;
  codesPerGlossary: number;
}

type GlossaryTableProps = {
  searchParams: { search: string };
  platformsGlossaryList: GlossaryListQueryResult;
  channelsGlossaryList: GlossaryListQueryResult;
  platformsByName: Record<string, PlatformCatalogItem>;
  channelsByName: Record<string, ChannelCatalogItem>;
};

export const GlossaryTable = ({
  searchParams,
  platformsGlossaryList,
  channelsGlossaryList,
  platformsByName,
  channelsByName,
}: GlossaryTableProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const gridRef = useRef<AgGridReact>(null);

  const rowData = useMemo((): GlossaryRowData[] => {
    const rows: GlossaryRowData[] = [];

    // Add channel rows
    for (const channelGlossary of channelsGlossaryList.items) {
      const channel = channelsByName[channelGlossary.name];
      if (!channel) {
        continue;
      }

      rows.push({
        id: channelGlossary.id,
        name: channelGlossary.name,
        label: channel.label,
        type: 'channel',
        group: t('Channels'),
        logo: channel.logo ? (
          <Icon component={channel.logo} width={16} height={16} />
        ) : null,
        glossaryItem: channelGlossary,
        aliases: channelGlossary.codes,
        codesPerGlossary: channelsGlossaryList.additional.codesPerGlossary,
      });
    }

    // Add platform rows
    for (const platformGlossary of platformsGlossaryList.items) {
      const platform = platformsByName[platformGlossary.name];

      if (!platform) {
        continue;
      }

      rows.push({
        id: platformGlossary.id,
        name: platformGlossary.name,
        label: platform.label,
        type: 'platform',
        group: t('Platforms'),
        logo: platform.logo ? (
          <img src={platform.logo} width={16} height={16} alt="platform logo" />
        ) : null,
        aliases: platformGlossary.codes,
        glossaryItem: platformGlossary,
        codesPerGlossary: platformsGlossaryList.additional.codesPerGlossary,
      });
    }

    const filteredRows = rows.filter((row) => {
      const isMatchingSearchValue = normalizeString(row.label).includes(
        normalizeString(searchParams.search),
      );

      return isMatchingSearchValue;
    });

    return filteredRows;
  }, [
    t,
    searchParams.search,
    channelsGlossaryList,
    platformsGlossaryList,
    channelsByName,
    platformsByName,
  ]);

  const defaultColDef = useMemo(
    (): ColDef<GlossaryRowData> => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      cellClass: '!flex !justify-start !items-center',
    }),
    [],
  );

  const columnDefs = useMemo(
    (): ColDef<GlossaryRowData>[] => [
      {
        colId: 'group',
        field: 'group',
        hide: true,
        rowGroup: true,
        flex: 1,
      },
      {
        field: 'aliases',
        headerName: t('Alias'),
        cellRenderer: AliasCellRenderer,
        resizable: false,
        flex: 1,
      },
    ],
    [t],
  );

  return (
    <AgGridReact<GlossaryRowData>
      ref={gridRef}
      className="[&_.ag-root.ag-unselectable]:!select-auto"
      loading={false}
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      rowModelType="clientSide"
      rowData={rowData}
      getRowId={(params) => params.data.id}
      suppressCellFocus
      groupDefaultExpanded={1}
      autoGroupColumnDef={{
        headerName: t('Item'),
        field: 'name',
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: PlatformItemCellRenderer,
        },
      }}
      domLayout="normal"
      noRowsOverlayComponent={GridEmptyState}
    />
  );
};

const PlatformItemCellRenderer = ({
  value,
  data,
  node,
}: CustomCellRendererProps<GlossaryRowData>) => {
  if (node.group) {
    return value; // For group headers, just show the group name
  }

  if (!data) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5 py-1.5">
      {data.logo || (
        <Settings className="fill-icon-primary" width={16} height={16} />
      )}
      <span
        className={typographyVariants({
          variant: 'bodyShort01',
          color: 'primary',
        })}
      >
        {data.label}
      </span>
    </div>
  );
};

const TruncatedBadgeList = ({
  aliases,
  onSelect,
}: {
  aliases: Array<{ id: string; name: string }>;
  onSelect: (index: number) => void;
}) => {
  const { t } = useTranslation('@myn/import-hub');

  const containerRef = useRef<HTMLDivElement>(null);
  const { visibleCount, hasOverflow } = useTruncatedList(containerRef, aliases);

  const truncatedItemsCount = aliases.length - visibleCount;

  return (
    <div
      ref={containerRef}
      className="flex w-full items-center gap-2 overflow-hidden"
    >
      {aliases.map((alias, index) => (
        <Chip
          key={alias.id}
          variant="outline"
          size="small"
          color="secondary"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(index);
          }}
          className={cn('shrink-0', {
            'invisible absolute': index >= visibleCount,
          })}
        >
          {alias.name}
        </Chip>
      ))}
      <Chip
        variant="outline"
        size="small"
        color="secondary"
        className={cn('shrink-0', { 'invisible absolute': !hasOverflow })}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(visibleCount);
        }}
      >
        {t('+{{truncatedItemsCount}} aliases', { truncatedItemsCount })}
      </Chip>
    </div>
  );
};

const AliasCellRenderer = memo(
  ({ data }: CustomCellRendererProps<GlossaryRowData>) => {
    const { t } = useTranslation('@myn/import-hub');
    const isReadOnlyUser = useIsReadOnlyUser();

    const [selectedAliasIndex, setSelectedAliasIndex] = useState<number | null>(
      null,
    );

    if (!data) return null;

    const openAliasesForm = () => {
      setSelectedAliasIndex(0);
    };

    const closeAliasesForm = () => {
      setSelectedAliasIndex(null);
    };

    return (
      <Popover
        open={!isReadOnlyUser && selectedAliasIndex !== null}
        onOpenChange={(open) => {
          if (isReadOnlyUser) return;
          open ? openAliasesForm() : closeAliasesForm();
        }}
      >
        <PopoverTrigger className="flex w-full cursor-pointer items-center gap-2 py-1.5">
          {data.aliases.length ? (
            <TruncatedBadgeList
              aliases={data.aliases}
              onSelect={(index) => {
                setSelectedAliasIndex(index);
              }}
            />
          ) : (
            <span
              className={typographyVariants({
                variant: 'bodyShort01',
                color: 'placeholder',
              })}
            >
              {t('Add alias')}
            </span>
          )}
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            align="start"
            side="bottom"
            className="z-popover max-h-[--radix-popper-available-height] w-[472px] overflow-y-auto p-3"
          >
            <GlossaryAliasesForm
              glossary={data.glossaryItem}
              codesPerGlossary={data.codesPerGlossary}
              autoFocusIndex={selectedAliasIndex}
              onClose={closeAliasesForm}
            />
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    );
  },
);
